import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpRequest,
  HttpResponse,
  HttpEvent,
  HttpHandlerFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthSignalService } from '../state/auth.signal';
import { PushNotificationService } from '../services/push-notification.service';
import { SocketService } from '../socket/socket.service';
import { environment } from '../../../environments/environment';
import { catchError, filter, map, switchMap, take, finalize } from 'rxjs/operators';
import { Observable, throwError, Subject, of } from 'rxjs';

/**
 * Renovación automática de la sesión.
 *
 * El access token dura 15 minutos, así que un 401 casi siempre significa
 * "caducó, renuévalo" y no "cierra la sesión". Sin esta lógica el agente sería
 * expulsado al login cada cuarto de hora, por muy larga que sea la sesión
 * configurada en el backend (REFRESH_TOKEN_DAYS).
 *
 * Si varias peticiones fallan a la vez solo debe dispararse UN /auth/refresh:
 * las demás esperan su resultado. Sin esta cola, N peticiones simultáneas
 * lanzarían N rotaciones del refresh token y la detección de reuso del backend
 * cerraría la sesión por error, justo lo contrario de lo que se busca.
 */
/**
 * Cómo acabó el intento de renovar.
 *
 * La distinción es la que separa "te echo al login" de "espera y reintenta", y
 * no es un matiz: cuando el backend no responde —o nginx corta antes de
 * llegar— la sesión del agente puede seguir siendo perfectamente válida.
 * Tratarlo como sesión caducada lo expulsa y, como al volver a entrar falla
 * igual, lo deja en un bucle de logout del que no sale hasta que el servidor
 * se recupera.
 */
type ResultadoRenovacion = 'ok' | 'sesion-invalida' | 'servidor-caido';

let renovando = false;
const renovacion$ = new Subject<ResultadoRenovacion>();

/** Rutas donde un 401 significa "credenciales inválidas", no "sesión caducada". */
function esIntentoDeAutenticacion(url: string): boolean {
  return url.includes('/auth/login') || url.includes('/auth/google') || url.includes('/auth/refresh');
}

function conCredenciales(req: HttpRequest<unknown>): HttpRequest<unknown> {
  return req.clone({
    withCredentials: true,
    setHeaders: { 'ngrok-skip-browser-warning': 'true' },
  });
}

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
  const authSignal = inject(AuthSignalService);
  const router = inject(Router);
  const pushService = inject(PushNotificationService);
  const socketService = inject(SocketService);

  // Attach credentials so the browser sends the HttpOnly cookie automatically.
  // No need to read or attach the token manually — the cookie travels with the request.
  const cloned = conCredenciales(req);

  const cerrarSesion = () => {
    // Desconectar socket para que el servidor marque al usuario como offline
    socketService.disconnect();
    pushService.logout();
    authSignal.logout();
    router.navigate(['/login']);
  };

  return next(cloned).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || esIntentoDeAutenticacion(req.url)) {
        return throwError(() => error);
      }

      // Ya hay una renovación en curso: esperar su desenlace y reintentar.
      if (renovando) {
        return renovacion$.pipe(
          take(1),
          switchMap((r) => (r === 'ok' ? next(conCredenciales(req)) : throwError(() => error))),
        );
      }

      renovando = true;
      return renovarSesion(next).pipe(
        switchMap((resultado) => {
          renovacion$.next(resultado);

          // Sesión renovada: reintentar la petición original una sola vez.
          if (resultado === 'ok') return next(conCredenciales(req));

          // El refresh caducó o fue revocado: ahora sí, al login.
          if (resultado === 'sesion-invalida') cerrarSesion();

          // 'servidor-caido' cae aquí SIN cerrar sesión: el error se propaga y
          // la próxima petición volverá a intentarlo. Al agente le fallará algo
          // puntual, pero no pierde la sesión ni el trabajo que tenga a medias.
          return throwError(() => error);
        }),
        finalize(() => {
          renovando = false;
        }),
      );
    }),
  );
};

/**
 * Llama a POST /auth/refresh reutilizando la cadena `next` en vez de inyectar
 * HttpClient (inyectarlo dentro de un interceptor provoca recursión de DI).
 */
function renovarSesion(next: HttpHandlerFn): Observable<ResultadoRenovacion> {
  const peticion = new HttpRequest('POST', `${environment.apiUrl}/auth/refresh`, {}, { withCredentials: true });
  return next(peticion).pipe(
    filter((evento: HttpEvent<unknown>) => evento instanceof HttpResponse),
    take(1),
    map(() => 'ok' as const),
    catchError((e: HttpErrorResponse) =>
      of(
        // Solo el backend diciendo "esta credencial no vale" cierra la sesión.
        // Un 5xx, un 429 o un status 0 (sin red, CORS, servidor caído) hablan
        // del servidor, no del agente. Y el 503 lo puede generar el propio
        // nginx sin llegar siquiera al backend.
        e.status === 401 || e.status === 403 ? 'sesion-invalida' as const : 'servidor-caido' as const,
      ),
    ),
  );
}
